/**
 * PanoramaViewer - 360度パノラマ表示コンポーネント
 * 複数の画像を1枚の連続したパノラマとして表示し、無限ループスクロールを実現
 */

import { useRef, useEffect, useState, useCallback } from 'react'
import { useImage } from '../hooks/useImage'
import { useStore } from '../../../store'
import type { SetItem } from '../../../core/types'

interface PanoramaViewerProps {
  items: SetItem[]
  currentIndex: number
  isPortrait: boolean
}

// 個別の画像コンポーネント
function PanoramaImage({ item }: { item: SetItem }) {
  const { url: imageUrl, isLoading, isError } = useImage(item.imageId)
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    )
  }
  
  if (isError || !imageUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900">
        <span className="text-gray-500">読み込みエラー</span>
      </div>
    )
  }
  
  return (
    <img
      src={imageUrl}
      alt={`Panorama segment ${item.order}`}
      className="h-full w-auto object-cover"
      draggable={false}
    />
  )
}

export function PanoramaViewer({ items, currentIndex, isPortrait }: PanoramaViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const imagesContainerRef = useRef<HTMLDivElement>(null)
  const isInitializedRef = useRef(false) // 初期スクロール設定済みフラグ
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  
  // Store methods
  const setCurrentIndex = useStore(state => state.setCurrentIndex)
  
  // 拡大率（縦持ち時）- 画像の高さを画面幅に合わせる
  const scale = isPortrait ? 1.6 : 1
  
  // 初回マウント時のみスクロール位置を設定 - requestAnimationFrameで次のフレームまで遅延
  useEffect(() => {
    // 既に初期化済みの場合は何もしない
    if (isInitializedRef.current) return
    
    // 次のフレームまで待って実行
    const rafId = requestAnimationFrame(() => {
      if (!scrollRef.current || !imagesContainerRef.current || items.length === 0) return
      if (isInitializedRef.current) return // 再度チェック
      
      const containerWidth = scrollRef.current.clientWidth
      const itemWidth = containerWidth // 各画像は画面幅と同じ
      const targetScroll = (currentIndex + 1) * itemWidth // +1 because we have a clone at the beginning
      
      scrollRef.current.scrollLeft = targetScroll
      isInitializedRef.current = true // 初期化完了フラグを立てる
    })
    
    return () => {
      cancelAnimationFrame(rafId)
    }
  }, []) // 空の依存配列で初回マウント時のみ実行
  
  // currentIndexの変更に応じてスクロール位置を更新（一方向データフロー）
  useEffect(() => {
    // 初期化が完了していない場合、またはドラッグ中の場合はスキップ
    if (!isInitializedRef.current || isDragging) return
    if (!scrollRef.current || items.length === 0) return
    
    const containerWidth = scrollRef.current.clientWidth
    const itemWidth = containerWidth
    const targetScroll = (currentIndex + 1) * itemWidth // +1 for the clone at the beginning
    
    // スムーズなアニメーションで位置を更新
    scrollRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    })
  }, [currentIndex, items.length, isDragging])
  
  // マウス/タッチイベントハンドラー
  const handleStart = useCallback((clientX: number) => {
    if (!scrollRef.current) return
    setIsDragging(true)
    setStartX(clientX - scrollRef.current.offsetLeft)
    setScrollLeft(scrollRef.current.scrollLeft)
  }, [])
  
  const handleMove = useCallback((clientX: number) => {
    if (!isDragging || !scrollRef.current) return
    
    const x = clientX - scrollRef.current.offsetLeft
    const walk = (x - startX) * 2 // スクロール速度調整
    scrollRef.current.scrollLeft = scrollLeft - walk
  }, [isDragging, startX, scrollLeft])
  
  const handleEnd = useCallback(() => {
    if (!isDragging || !scrollRef.current || items.length === 0) return
    
    setIsDragging(false)
    
    // 現在のスクロール位置からインデックスを計算
    const containerWidth = scrollRef.current.clientWidth
    const itemWidth = containerWidth
    const currentScroll = scrollRef.current.scrollLeft
    
    // インデックスを計算（クローン分を考慮）
    let newIndex = Math.round(currentScroll / itemWidth) - 1
    
    // 無限ループ処理とStore更新
    if (newIndex < 0) {
      newIndex = items.length - 1
      scrollRef.current.scrollLeft = (items.length) * itemWidth
    } else if (newIndex >= items.length) {
      newIndex = 0
      scrollRef.current.scrollLeft = itemWidth
    }
    
    // Store経由でインデックスを更新
    if (newIndex !== currentIndex) {
      setCurrentIndex(newIndex)
    }
  }, [isDragging, items.length, currentIndex, setCurrentIndex])
  
  // スクロールの無限ループ処理（Storeのインデックスを更新）
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || isDragging || items.length === 0) return
    
    const containerWidth = scrollRef.current.clientWidth
    const itemWidth = containerWidth
    const currentScroll = scrollRef.current.scrollLeft
    const maxScroll = itemWidth * (items.length + 1) // +2 for clones, -1 for 0-index
    
    // 無限ループの境界処理
    if (currentScroll <= 0) {
      // 左端に到達したら最後の画像へジャンプ
      scrollRef.current.scrollLeft = items.length * itemWidth
      if (currentIndex !== items.length - 1) {
        setCurrentIndex(items.length - 1)
      }
    } else if (currentScroll >= maxScroll) {
      // 右端に到達したら最初の画像へジャンプ
      scrollRef.current.scrollLeft = itemWidth
      if (currentIndex !== 0) {
        setCurrentIndex(0)
      }
    } else {
      // 通常のスクロール時のインデックス更新
      const newIndex = Math.round(currentScroll / itemWidth) - 1
      if (newIndex >= 0 && newIndex < items.length && newIndex !== currentIndex) {
        setCurrentIndex(newIndex)
      }
    }
  }, [items.length, isDragging, currentIndex, setCurrentIndex])
  
  // タッチイベント
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches[0]) {
      handleStart(e.touches[0].clientX)
    }
  }
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches[0]) {
      handleMove(e.touches[0].clientX)
    }
  }
  
  // マウスイベント
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    handleStart(e.clientX)
  }
  
  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault()
    handleMove(e.clientX)
  }
  
  const handleMouseUp = () => {
    handleEnd()
  }
  
  const handleMouseLeave = () => {
    handleEnd()
  }
  
  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full bg-black overflow-hidden"
    >
      {/* パノラマコンテナ */}
      <div
        ref={scrollRef}
        className="h-full overflow-x-auto overflow-y-hidden scrollbar-hide"
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: isDragging ? 'auto' : 'smooth'
        }}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* 画像を横に連結 */}
        <div ref={imagesContainerRef} className="flex h-full">
          {/* 最後の画像を最初に複製（左端のループ用） */}
          {items.length > 0 && (() => {
            const lastItem = items[items.length - 1]
            if (lastItem) {
              return (
                <PanoramaImage 
                  item={lastItem}
                />
              )
            }
            return null
          })()}
          
          {/* 実際の画像 */}
          {items.map((item) => (
            <PanoramaImage 
              key={item.id}
              item={item}
            />
          ))}
          
          {/* 最初の画像を最後に複製（右端のループ用） */}
          {items.length > 0 && (() => {
            const firstItem = items[0]
            if (firstItem) {
              return (
                <PanoramaImage 
                  item={firstItem}
                />
              )
            }
            return null
          })()}
        </div>
      </div>
      
      {/* 位置インジケーター（currentIndexを使用） */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 px-3 py-1 rounded-full">
        <span className="text-white text-sm">
          {currentIndex + 1} / {items.length}
        </span>
      </div>
      
      {/* 拡大率インジケーター（縦持ち時のみ） */}
      {isPortrait && (
        <div className="absolute top-4 right-4 bg-black/60 px-3 py-1 rounded-full">
          <span className="text-white text-sm">
            {Math.round(scale * 100)}%
          </span>
        </div>
      )}
    </div>
  )
}

// スクロールバーを非表示にするCSS
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`

// スタイルを動的に追加
if (typeof document !== 'undefined') {
  const styleId = 'panorama-viewer-styles'
  if (!document.getElementById(styleId)) {
    const styleElement = document.createElement('style')
    styleElement.id = styleId
    styleElement.textContent = scrollbarHideStyles
    document.head.appendChild(styleElement)
  }
}